import React from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, ArrowRight, Check, AlertCircle } from "lucide-react";
import PageShell from "../components/PageShell";
import { Section, SectionHeading, Reveal } from "../lib/ui";
import { SERVICE_CATEGORIES } from "../config";
import { SERVICE_IMAGES } from "../lib/images";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const cat = SERVICE_CATEGORIES.find((c) => c.slug === slug);
  if (!cat) return <Navigate to="/services" replace />;

  return (
    <PageShell>
      <section className="relative px-6 md:px-10 lg:px-16 pt-36 pb-4 md:pt-40 overflow-hidden" data-testid="detail-hero">
        <div className="absolute inset-0 pointer-events-none">
          <img src={SERVICE_IMAGES[cat.slug]} alt="" className="w-full h-full object-cover opacity-25" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
        </div>
        <div className="max-w-7xl mx-auto relative">
          <div className="absolute -top-24 right-0 h-72 w-72 rounded-full bg-gold/5 blur-3xl pointer-events-none" />
          <button onClick={() => navigate("/services")} className="inline-flex items-center gap-2 text-silver/60 hover:text-gold transition-colors text-sm font-body mb-8" data-testid="detail-back">
            <ArrowLeft size={16} className="rtl-flip" /> All services
          </button>
          <SectionHeading overline={cat.label} title={<span className="gold-text">{cat.tagline}</span>} kicker={cat.intro} />
          {cat.process && (
            <Reveal delay={0.12}>
              <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-3" data-testid="detail-process">
                {cat.process.map((s, i) => (
                  <React.Fragment key={s}>
                    <span className="rounded-full border border-gold/25 bg-gold/5 px-4 py-2 text-sm font-body text-gold-light">{s}</span>
                    {i < cat.process.length - 1 && <ArrowRight size={13} className="text-silver/40 rtl-flip" />}
                  </React.Fragment>
                ))}
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <Section>
        <div className="grid md:grid-cols-2 gap-5">
          {cat.items.map((item, i) => (
            <Reveal key={item.title} delay={(i % 2) * 0.08}>
              <div className="group h-full hairline rounded-xl bg-ink-surface/60 p-7 hover:border-gold/40 transition-colors duration-500" data-testid={`detail-item-${i}`}>
                <div className="flex items-start gap-4">
                  <span className="mt-1 h-9 w-9 shrink-0 rounded-full border border-gold/30 flex items-center justify-center text-gold">
                    <Check size={16} strokeWidth={2} />
                  </span>
                  <div>
                    <h3 className="font-display font-light text-xl text-white group-hover:text-gold-light transition-colors">{item.title}</h3>
                    <p className="mt-3 text-silver/65 font-body font-light text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {cat.note && (
          <Reveal delay={0.1}>
            <div className="mt-6 flex items-start gap-3 rounded-xl border border-white/10 bg-ink-surface/40 p-5" data-testid="detail-note">
              <AlertCircle size={18} className="text-gold shrink-0 mt-0.5" />
              <p className="text-silver/60 font-body font-light text-sm leading-relaxed">{cat.note}</p>
            </div>
          </Reveal>
        )}

        {/* Get in contact CTA */}
        <Reveal delay={0.1}>
          <div className="mt-10 relative overflow-hidden rounded-2xl border border-gold/25 bg-gold/[0.04] px-8 py-12 md:py-16 text-center">
            <div className="absolute -top-16 left-1/2 -translate-x-1/2 h-40 w-40 rounded-full bg-gold/10 blur-3xl" />
            <h2 className="relative font-display font-light text-3xl md:text-4xl tracking-tight">
              Interested in <span className="gold-text">{cat.label}?</span>
            </h2>
            <p className="relative mt-4 text-silver/70 font-body font-light max-w-xl mx-auto">
              Get in contact to discuss your requirements and we'll design a considered, bespoke approach.
            </p>
            <motion.button
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/contact")}
              className="gold-sweep relative mt-8 inline-flex items-center gap-2 rounded-full bg-gold text-black px-8 py-4 text-sm font-body font-medium tracking-wide hover:bg-gold-light transition-colors"
              data-testid="detail-cta-contact"
            >
              Get in Contact <ArrowUpRight size={18} />
            </motion.button>
          </div>
        </Reveal>
      </Section>
    </PageShell>
  );
}
